<template>
    <div>
        <v-container fluid :style="{ 'min-height': '800px', 'background-size': 'contain', 'background-color': '#cdd5df' }">
            <v-container class="contain-width">
              <v-row>
                <v-col md="4">
                  <h1 class="text-h5 black--text font-weight-bold">{{ $t('headings.Dashboard') }}: <span v-if="myOrganisation">{{ myOrganisation.name }}</span></h1>
                  <h2 v-if="myOrganisation && myOrganisation.affiliate" class="text-subtitle-1 black--text mt-2">
                    {{ $t('headings.Super partner') }}
                    <v-tooltip right content-class="info tooltip-right tooltip"
                               max-width="400">
                      <template v-slot:activator="{ on }">
                        <v-icon color="primary" v-on="on">mdi-medal</v-icon>
                      </template>
                      <span>{{ $t('Your organisation is an super partner') }}. {{ $t('Other organisations can associate themselves with you as sub partners') }}.</span>
                    </v-tooltip>
                  </h2>

                  <h2 v-if="myOrganisation && myOrganisation.affiliate_partner" class="text-subtitle-1 black--text">
                    {{ $t('headings.Partner, affiliated with') }}: {{ myOrganisation.affiliate_partner.name }}
                    <v-tooltip right content-class="info tooltip-right tooltip"
                               max-width="400">
                      <template v-slot:activator="{ on }">
                        <v-icon color="primary" v-on="on">info</v-icon>
                      </template>
                      <span>{{ $t('As a partner you can invite member, and scan tags, to keep your lost property in order') }}.</span>
                    </v-tooltip>
                  </h2>
                  <h2 v-if="!adminForOrganisations[0].affiliate && !adminForOrganisations[0].affiliate_partner" class="text-subtitle-1 black--text">
                    {{ $t('headings.Partner') }}
                    <v-tooltip right content-class="info tooltip-right tooltip"
                               max-width="400">
                      <template v-slot:activator="{ on }">
                        <v-icon color="primary" v-on="on">info</v-icon>
                      </template>
                      <span>{{ $t('As a partner you can invite users, and scan tags, to keep your lost property in order') }}.</span>
                    </v-tooltip>
                  </h2>

                  <h2 class="text-subtitle-1 black--text">{{ $t('Copy your landing page link') }}
                    <v-tooltip right content-class="info tooltip-right tooltip">
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn v-if="myOrganisation"
                               v-bind="attrs"
                               v-on="on"
                               class="align-self-center ml-2"
                               v-clipboard:copy="shareLink"
                               v-clipboard:success="onCopy"
                               v-clipboard:error="onError"
                               fab
                               small
                        ><v-icon>mdi-share-variant</v-icon></v-btn>
                      </template>
                      <div>{{ $t('Click to copy invite link for your organisation') }}. {{ $t('Paste this link in your own email to invite members') }}.</div>
                    </v-tooltip>
                  </h2>

                </v-col>
                <v-col md="12" lg="2">
                  <div>
                    <p class="text-subtitle-1 black--text">{{ $t('Account Strength') }}</p>
                    <v-progress-linear
                        v-model="strength"
                        color="success"
                        height="25"
                        class="mb-6"
                    >
                      <template v-slot:default="{ value }">
                        <strong>{{ Math.ceil(value) }}%</strong>
                      </template>
                    </v-progress-linear>
                  </div>
                </v-col>
                <v-col md="12" lg="6">
                  <v-stepper light non-linear elevation="0" flat rounded alt-labels style="background-color: transparent; box-shadow: none" class="d-none d-md-block">
                    <v-stepper-header>
                      <v-stepper-step
                          :complete="true"
                          color="success"
                          step="1"
                      >
                        {{ $t('Account enabled') }}
                      </v-stepper-step>

                      <v-divider></v-divider>

                      <v-stepper-step
                          :complete="testscanComplete"
                          color="success"
                          step="2"
                      >
                        {{ $t('Test scan') }}
                      </v-stepper-step>

                      <v-divider></v-divider>

                      <v-stepper-step
                          :complete="hasMembers"
                          color="success"
                          step="3"
                      >
                        {{ $t('Members invited') }}
                      </v-stepper-step>

                      <v-divider v-if="myOrganisation.affiliate"></v-divider>

                      <v-stepper-step
                          v-if="myOrganisation.affiliate"
                          :complete="hasPartners"
                          color="success"
                          step="4"
                      >
                        {{ $t('Build your community') }}
                      </v-stepper-step>

                    </v-stepper-header>
                  </v-stepper>
                </v-col>
              </v-row>
              <v-row>
                <v-col
                    md="4"
                    sm="12"
                >
                  <v-card elevation="0" style="border-radius: 10px !important;">
                    <v-card-text v-if="adminForOrganisations[0].logo" class="d-flex justify-space-between">
                        <v-img
                                v-if="adminForOrganisations[0].logo"
                                max-height="250"
                                max-width="250"
                                class="mt-1 image-container"
                                :src="'https://images.findmykit.com/' + adminForOrganisations[0].logo"
                        >

                        </v-img>

                        <v-btn
                            color="primary"
                            dark
                            text
                            icon
                            @click.stop="editOrganisation"
                        >
                          <v-icon>edit</v-icon>
                        </v-btn>
                    </v-card-text>
                    <v-btn
                        v-else
                        color="primary"
                        icon
                        @click.stop="editOrganisation"
                        class="float-right mt-3 mr-3"
                    >
                      <v-icon>edit</v-icon>
                    </v-btn>

                    <v-card-title class="flex justify-space-between">
                      <div class="primary--text">{{ $t('Registered details') }}</div>
                    </v-card-title>

                    <v-card-text v-if="myOrganisation">
                      <p class="text-body-1">
                        <span class="font-weight-bold">{{ $t('Name') }}:</span> {{ myOrganisation.name }}<br>
                        <span class="font-weight-bold">{{ $t('Phone number') }}:</span> {{ myOrganisation.phone }}<br>
                        <span v-if="myOrganisation.organisation_category"><span class="font-weight-bold">{{ $t('Type') }}:</span> {{ myOrganisation.organisation_category.name }}<br></span>
                        <span v-if="myOrganisation.affiliate_partner"><span class="font-weight-bold">{{ $t('Super partner') }}:</span> {{ myOrganisation.affiliate_partner.name }}<br></span>
                        <span class="font-weight-bold">{{ $t('Address') }}:</span><br>
                        {{ myOrganisation.address1 }}<br>
                        <span v-if="myOrganisation.address2">{{ myOrganisation.address2 }}<br></span>
                        {{ myOrganisation.town }}<br>
                        {{ myOrganisation.county }}<br>
                        {{ myOrganisation.postcode }}<br>
                      </p>
                    </v-card-text>
                    <v-sheet
                        v-else
                        :color="`grey lighten-4`"
                        class="px-3 pt-3 pb-3"
                    >
                      <v-skeleton-loader
                          class="mx-auto"
                          type="card"
                      ></v-skeleton-loader>
                    </v-sheet>
                  </v-card>

                  <v-card elevation="0" style="border-radius: 10px !important;" class="mt-6">
                    <v-card-title class="d-flex justify-space-between">
                      <div class="text-h6">{{ $t('headings.Building your community') }}</div>
                      <div id="tooltip6">
                        <Tooltip
                            :title="$t('Build your community')"
                            :content="$t('Click here for more information on how to build your community')"
                            :show="tooltips[5]"
                            :currentTooltip="(myOrganisation && myOrganisation.affiliate) ? currentTooltip : currentTooltip - 1"
                            :tooltipsLength="tooltipsCount"
                            v-on:end-tips="endTips()"
                            v-on:next-tip="nextTip()"
                            v-on:click-button="communityDialog = true"
                            :action="false"
                        >
                        </Tooltip>
                      </div>
                    </v-card-title>
                    <v-card-text>
                      <p class="text-body-1">{{ $t('Building your community is an integral part of using the {brand} platform', {brand: brandName}) }}. <v-btn
                          color="primary"
                          small
                          @click="communityDialog = true"
                          class="mt-2 cta-button float-right"
                      >{{ $t('buttons.Learn more') }}</v-btn></p>
                    </v-card-text>
                    <v-card-text>
                      <v-card
                          elevation="0"
                          color="#E8E8E8"
                          style="border-radius: 10px !important;"
                          class="mx-auto mt-6"
                          id="tooltip7"
                      >
                        <v-toolbar
                            color="success"
                            dark
                        >
                          <div class="d-flex justify-space-between" style="width: 100%">
                            <div class="text-h6">{{ $t('headings.Invite members') }}</div>
                            <div>
                              <Tooltip
                                  :title="$t('headings.Invite members')"
                                  :content="$t('Invite individuals to sign up as members with your organisation via {brand} to receive a commission on each subscription', {brand: brandName})"
                                  :show="tooltips[6]"
                                  :currentTooltip="(myOrganisation && myOrganisation.affiliate) ? currentTooltip : currentTooltip !== 0 ? - 1 : currentTooltip"
                                  :tooltipsLength="tooltipsCount"
                                  v-on:end-tips="endTips()"
                                  v-on:next-tip="nextTip()"
                                  :action="false"
                                  iconColor="white"
                              >
                              </Tooltip>
                            </div>
                          </div>
                        </v-toolbar>

                        <v-card-actions>
                          <v-btn
                              color="primary"
                              class="cta-button"
                              @click="usersDialog = true"
                          >
                            Invite <v-icon
                              right
                              dark
                          >
                            mdi-plus-circle
                          </v-icon>
                          </v-btn>

                          <v-spacer></v-spacer>

                          <v-btn
                              text
                              color="success"
                              @click="showMembers = !showMembers"
                          >
                            {{ $t('buttons.Learn more') }} <v-icon>{{ showMembers ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                          </v-btn>
                        </v-card-actions>

                        <v-expand-transition>
                          <div v-show="showMembers">
                            <v-divider></v-divider>

                            <v-card-text class="black--text">
                              <p class="text-body-1">{{ $t('Click invite, then enter one or more email addresses') }}. {{ $t('Recipients will then receive an email with a recommendation from your organisation') }}.</p>
                              <p class="text-body-1">{{ $t('You will also have the option to import a csv list of email addresses') }}.</p>
                              <p class="text-body-1">{{ $t('Alternatively a share button is provided; copy the link to post on your own emails / social media etc') }}.</p>
                              <p class="text-subtitle-1 black--text text-center">
                                <v-tooltip right content-class="info tooltip-right tooltip">
                                  <template v-slot:activator="{ on, attrs }">
                                    <v-btn v-if="myOrganisation"
                                           v-bind="attrs"
                                           v-on="on"
                                           class="align-self-center ml-2"
                                           v-clipboard:copy="shareLink"
                                           v-clipboard:success="onCopy"
                                           v-clipboard:error="onError"
                                           fab
                                           small
                                    ><v-icon>mdi-share-variant</v-icon></v-btn>
                                  </template>
                              <div>{{ $t('Click to copy invite link for your organisation') }}. {{ $t('Paste this link in your own email to invite members') }}.</div>
                                </v-tooltip>
                              </p>
                              <p class="text-body-1">{{ $t('Each member that subscribes under your organisation will earn you a commission') }}.</p>
                            </v-card-text>

                            <v-card-actions class="pa-4">
                              <v-btn
                                  class="cta-button"
                                  block
                                  color="primary"
                                  @click="usersDialog = true"
                              >
                                {{ $t('buttons.Invite') }} <v-icon
                                  right
                                  dark
                              >
                                mdi-plus-circle
                              </v-icon>
                              </v-btn>
                            </v-card-actions>

                          </div>
                        </v-expand-transition>
                      </v-card>

                      <v-card
                          v-if="myOrganisation && myOrganisation.affiliate"
                          elevation="0" style="border-radius: 10px !important;"
                          color="#E8E8E8"
                          class="mx-auto mt-6"
                          id="tooltip8"
                      >
                        <v-toolbar
                            color="success"
                            dark
                        >
                          <div class="d-flex justify-space-between" style="width: 100%">
                            <div class="text-h6">{{ $t('headings.Invite sub-partners') }}</div>
                            <div>
                              <Tooltip
                                  :title="$t('headings.Invite sub-partners')"
                                  :content="$t('Create partners by inviting organisations to sign up')"
                                  :show="tooltips[7]"
                                  :currentTooltip="currentTooltip"
                                  :tooltipsLength="tooltipsCount"
                                  v-on:end-tips="endTips()"
                                  v-on:next-tip="nextTip()"
                                  :action="false"
                                  iconColor="white"
                              >
                              </Tooltip>
                            </div>
                          </div>
                        </v-toolbar>

                        <v-card-actions>
                          <v-btn
                              color="primary"
                              class="cta-button"
                              @click="affiliatesDialog = true"
                          >
                            {{ $t('buttons.Invite') }} <v-icon
                              right
                              dark
                          >
                            mdi-plus-circle
                          </v-icon>
                          </v-btn>

                          <v-spacer></v-spacer>

                          <v-btn
                              text
                              color="success"
                              @click="showPartners = !showPartners"
                          >
                            {{ $t('buttons.Learn more') }} <v-icon>{{ showPartners ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                          </v-btn>
                        </v-card-actions>

                        <v-expand-transition>
                          <div v-show="showPartners">
                            <v-divider></v-divider>

                            <v-card-text class="black--text">
                              <p class="text-body-1">{{ $t('As a super partner you can invite sub-partners to join your community at {brand}', {brand: brandName}) }}.</p>
                              <p class="text-body-1">{{ $t('A sub partner is an organisation who has joined {brand} through your invitation', {brand: brandName}) }}. {{ $t('You will both receive a percentage of commission through any subscription sold to their members') }}.
                              <p class="text-body-1">{{ $t("For example, you sign up your child's school as a sub partner, who then sign up the parents of the children") }}. {{ $t('Both your organisation, and the school earn commission on each subscription paid') }}.
                              <p class="text-body-1">{{ $t('The sub partner will be responsible for managing its members and for signing up new members') }}.
                              <p class="text-body-1">{{ $t('Click invite, then enter one or more email addresses') }}. {{ $t('Recipients will then receive an email with a recommendation from your organisation') }}.</p>
                              <p class="text-body-1">{{ $t('Alternatively, a share button is provided; copy the link to post on your own emails / social media etc') }}.</p>
                            </v-card-text>

                            <v-card-actions class="pa-4">
                              <v-btn
                                  class="cta-button"
                                  block
                                  color="primary"
                                  @click="affiliatesDialog = true"
                              >
                                {{ $t('buttons.Invite') }} <v-icon
                                  right
                                  dark
                              >
                                mdi-plus-circle
                              </v-icon>
                              </v-btn>
                            </v-card-actions>

                          </div>
                        </v-expand-transition>
                      </v-card>

                      <v-card
                          elevation="0" style="border-radius: 10px !important;"
                          color="#E8E8E8"
                          class="mx-auto mt-6"
                          id="tooltip9"
                      >
                        <v-toolbar
                            color="success"
                            dark
                        >
                          <div class="d-flex justify-space-between" style="width: 100%">
                            <div class="text-h6">{{ $t('headings.Download marketing pack') }}</div>
                            <div>
                              <Tooltip
                                  :title="$t('headings.Download marketing pack')"
                                  :content="$t('Download a marketing pack to promote {brand}', {brand: brandName})"
                                  :show="tooltips[8]"
                                  :currentTooltip="(myOrganisation && myOrganisation.affiliate) ? currentTooltip : currentTooltip !== 0 ? - 2 : currentTooltip"
                                  :tooltipsLength="tooltipsCount"
                                  v-on:end-tips="endTips()"
                                  v-on:next-tip="nextTip()"
                                  :action="false"
                                  iconColor="white"
                              >
                              </Tooltip>
                            </div>
                          </div>
                        </v-toolbar>

                        <v-card-actions>
                          <v-btn
                              color="primary"
                              class="cta-button"
                              @click="downloadMediaPack"
                          >
                            {{ $t('buttons.Download') }} <v-icon
                              right
                              dark
                          >
                            mdi-file-download
                          </v-icon>
                          </v-btn>

                          <v-spacer></v-spacer>

                          <v-btn
                              text
                              color="success"
                              @click="showMarketing = !showMarketing"
                          >
                            {{ $t('buttons.Learn more') }} <v-icon>{{ showMarketing ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                          </v-btn>
                        </v-card-actions>

                        <v-expand-transition>
                          <div v-show="showMarketing">
                            <v-divider></v-divider>

                            <v-card-text class="black--text">
                              <p class="text-body-1">{{ $t("We've made it easy to engage with potential new members and grow your community") }}. {{ $t("We have provided all the information you need in our downloadable marketing pack and we offer our full support along the way through") }} <a :href="'mailto:' + supportEmail">{{supportEmail}}</a>.</p>
                            </v-card-text>

                            <v-card-actions class="pa-4">
                              <v-btn
                                  class="cta-button"
                                  block
                                  color="primary"
                                  @click="downloadMediaPack"
                              >
                                {{ $t('buttons.Download') }} <v-icon
                                  right
                                  dark
                              >
                                mdi-file-download
                              </v-icon>
                              </v-btn>
                            </v-card-actions>

                          </div>
                        </v-expand-transition>
                      </v-card>
                    </v-card-text>
                  </v-card>

                  <v-dialog v-model="usersDialog" persistent max-width="600px">
                    <v-card>
                      <v-card-title class="d-flex justify-space-between">
                        <span class="headline">{{ $t('headings.Invite people to sign up') }}</span>
                        <v-tooltip top>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn v-if="myOrganisation"
                                   v-bind="attrs"
                                   v-on="on"
                                   class="align-self-center ml-6"
                                   v-clipboard:copy="shareLink"
                                   v-clipboard:success="onCopy"
                                   v-clipboard:error="onError"
                                   fab
                                   small
                            ><v-icon>mdi-share-variant</v-icon></v-btn>
                          </template>
                          <div>{{ $t('Click to copy invite link for your organisation') }}. {{ $t('Paste this link in your own email to invite members') }}.</div>
                        </v-tooltip>
                      </v-card-title>
                      <v-card-text>
                        <ValidationObserver ref="inviteObserver">
                          <v-form @submit.prevent="handleSubmitInvite">

                            <ValidationProvider vid="emails" name="emails" rules="required" v-slot="{ errors }">
                              <v-combobox
                                  v-model="emails"
                                  :items="emails"
                                  :search-input.sync="search"
                                  :hide-selected="hideSelected"
                                  :label="$t('labels.Add some email addresses')"
                                  :multiple="multiple"
                                  :small-chips="chips"
                                  clearable
                                  deletable-chips
                                  persistent-hint
                                  :hint="$t('hints.Recipients will receive an email with a signup link from your organisation')"
                              >
                                <template v-if="noData" v-slot:no-data>
                                  <v-list-item>
                                    <v-list-item-content>
                                      <v-list-item-title>
                                        {{ $t('Type email and press') }} <kbd>{{ $t('enter') }}</kbd> {{ $t('to create') }}
                                      </v-list-item-title>
                                    </v-list-item-content>
                                  </v-list-item>
                                </template>
                              </v-combobox>
                              <div class="v-text-field__details" v-if="errors">
                                <div class="v-messages theme--light error--text" role="alert">
                                  <div class="v-messages__wrapper">
                                    <div class="v-messages__message">{{ errors[0] }}</div>
                                  </div>
                                </div>
                              </div>
                            </ValidationProvider>

                            <usecsv-button
                                :importerKey="getImporterKey"
                                :user="{ userId: '12345' }"
                                :onData="onData"
                            >
                              {{ $t('buttons.Import Data') }}
                            </usecsv-button>

                            <br><br>

                          </v-form>
                        </ValidationObserver>
                      </v-card-text>
                      <v-card-actions class="d-flex justify-space-between">
                        <v-btn color="blue darken-1" text @click="handleCloseInvite">{{ $t('buttons.Close') }}</v-btn>
                        <v-btn color="primary" @click="handleSubmitInvite">{{ $t('buttons.Send') }}</v-btn>
                      </v-card-actions>
                    </v-card>
                  </v-dialog>

                  <v-dialog v-model="affiliatesDialog" persistent max-width="600px">
                    <v-card>
                      <v-card-title class="d-flex justify-space-between">
                        <span style="max-width: 450px" class="headline">{{ $t('headings.Invite sub-partners to sign up under your organisation') }}</span>
                        <v-tooltip top>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn v-if="myOrganisation"
                                   v-bind="attrs"
                                   v-on="on"
                                   class="align-self-center ml-6"
                                   v-clipboard:copy="shareLinkAffiliate"
                                   v-clipboard:success="onCopy"
                                   v-clipboard:error="onError"
                                   fab
                                   small
                            ><v-icon>mdi-share-variant</v-icon></v-btn>
                          </template>
                          <div>{{ $t('Click to copy your super partner invite link') }}. {{ $t('Paste this link in your own email to invite sub-partners associated to your organisation') }}.</div>
                        </v-tooltip>
                      </v-card-title>
                      <v-card-text>
                        <ValidationObserver ref="invitePartnersObserver">
                          <v-form @submit.prevent="handleSubmitInvitePartners">

                            <ValidationProvider vid="emails" :name="$t('labels.Emails')" rules="required" v-slot="{ errors }">
                              <v-combobox
                                  v-model="emails"
                                  :items="emails"
                                  :search-input.sync="search"
                                  :hide-selected="hideSelected"
                                  :label="$t('labels.Add some email addresses')"
                                  :multiple="multiple"
                                  persistent-hint
                                  :hint="$t('hints.Recipients will receive an email with a signup link from your organisation')"
                                  :small-chips="chips"
                                  clearable
                                  deletable-chips
                              >
                                <template v-if="noData" v-slot:no-data>
                                  <v-list-item>
                                    <v-list-item-content>
                                      <v-list-item-title>
                                        {{ $t('Type email and press') }} <kbd>{{ $t('enter') }}</kbd> {{ $t('to create') }}
                                      </v-list-item-title>
                                    </v-list-item-content>
                                  </v-list-item>
                                </template>
                              </v-combobox>
                              <div class="v-text-field__details" v-if="errors">
                                <div class="v-messages theme--light error--text" role="alert">
                                  <div class="v-messages__wrapper">
                                    <div class="v-messages__message">{{ errors[0] }}</div>
                                  </div>
                                </div>
                              </div>
                            </ValidationProvider>

                            <usecsv-button
                                :importerKey="getImporterKey"
                                :user="{ userId: '12345' }"
                                :onData="onData"
                            >
                              {{ $t('buttons.Import Data') }}
                            </usecsv-button>

                            <br><br>

                          </v-form>
                        </ValidationObserver>
                      </v-card-text>
                      <v-card-actions class="d-flex justify-space-between">
                        <v-btn color="blue darken-1" text @click="affiliatesDialog = false">{{ $t('buttons.Close') }}</v-btn>
                        <v-btn color="primary" @click="handleSubmitInvitePartners">{{ $t('buttons.Submit') }}</v-btn>
                      </v-card-actions>
                    </v-card>
                  </v-dialog>

                </v-col>
                <v-col
                    md="8"
                    sm="12"
                >
                  <v-card elevation="0" style="border-radius: 10px !important;" class="mb-6">
                    <v-card-text>
                    <h2 class="primary--text" id="tooltip1">
                      {{ $t('headings.Found an item?') }}?
                      <Tooltip
                          :title="$t('headings.Found an item?')"
                          :content="$t('Click in the box below, then scan a tag with your handheld scanner, or enter the code using your keypad')"
                          :show="tooltips[0]"
                          :currentTooltip="currentTooltip"
                          :tooltipsLength="tooltipsCount"
                          v-on:end-tips="endTips()"
                          v-on:next-tip="nextTip()"
                      ></Tooltip>
                      <FaqTooltip v-if="currentTooltip === 0" id="9"></FaqTooltip>
                    </h2>
                      <ValidationObserver ref="scanObserver">
                        <v-form @submit.prevent="handleSubmit" class="scan-form-full">
                          <ValidationProvider vid="code" :name="$t('labels.Code')" rules="required" v-slot="{ errors }">
                            <v-text-field
                                v-model="code"
                                :error-messages="errors[0]"
                                :placeholder="$t('placeholders.Enter code here')"
                                solo
                                required
                                filled
                                flat
                                clearable
                                background-color="#fff"
                            >
                              <template v-slot:append-outer>
                                <v-btn class="scan-button" @click="handleSubmit">{{ $t('buttons.Go') }}</v-btn>
                              </template>
                            </v-text-field>
                          </ValidationProvider>
                        </v-form>
                      </ValidationObserver>
                      <v-dialog v-model="foundDialog" max-width="600px">
                        <v-card
                            class="mx-auto"
                            v-if="found"
                        >
                          <v-card-title v-if="tagDetails.owner_name"> {{ tagDetails.owner_name }}</v-card-title>
                          <v-card-title v-else>{{ $t('Owner found') }}</v-card-title>
                          <v-card-text>
                            <ValidationObserver ref="notifyObserver">
                              <v-form @submit.prevent="handleNotify">
                                <ValidationProvider vid="item" :name="$t('labels.Item')" rules="required" v-slot="{ errors }">
                                  <v-combobox
                                      v-model="item"
                                      :error-messages="errors[0]"
                                      :items="getItems"
                                      :label="$t('labels.Item found description')"
                                      persistent-hint
                                      :hint="$t('hints.Select an item or type your own')"
                                      clearable
                                  ></v-combobox>
                                </ValidationProvider>
                                <p class="mt-3 mb-3">{{ $t('Collected from') }}: {{ this.myOrganisation.name }}</p>
                                <ValidationProvider vid="note" :name="$t('labels.Note for owner')" v-slot="{ errors }">
                                  <v-textarea
                                      v-model="note"
                                      :error-messages="errors[0]"
                                      :label="$t('labels.Note for owner')"
                                  ></v-textarea>
                                </ValidationProvider>
                              </v-form>
                            </ValidationObserver>
                          </v-card-text>
                          <v-card-actions>
                            <v-btn color="blue darken-1" text @click="foundDialog = false">{{ $t('buttons.Close') }}</v-btn>
                            <div class="flex-grow-1"></div>
                            <v-btn class="mx-3" color="primary" @click="handleNotify">{{ $t('buttons.Notify') }}</v-btn>
                          </v-card-actions>
                        </v-card>

                        <v-card
                            class="mx-auto"
                            v-if="notified"
                        >
                          <v-card-title>{{ notified }}</v-card-title>
                          <v-card-actions>
                            <div class="flex-grow-1"></div>
                            <v-btn color="blue darken-1" text @click="foundDialog = false">{{ $t('buttons.Close') }}</v-btn>
                          </v-card-actions>
                        </v-card>

                      </v-dialog>
                    </v-card-text>
                  </v-card>
                  <v-expansion-panels flat v-model="openedPanel" focusable>
                    <v-expansion-panel
                        id="tooltip2"
                        class="mb-4"
                        style="border-radius: 10px !important;border-color: blue"
                    >
                      <v-expansion-panel-header>
                        <div class="d-flex justify-space-between">
                          <div class="text-body-1 font-weight-bold">{{ $t('headings.Test scan') }}</div>
                          <v-spacer></v-spacer>
                          <Tooltip
                              :title="$t('headings.Test scan')"
                              :content="$t('To check that everything is working correctly, and to see how the process works, try a test scan')"
                              :show="tooltips[1]"
                              :currentTooltip="currentTooltip"
                              :tooltipsLength="tooltipsCount"
                              v-on:end-tips="endTips()"
                              v-on:next-tip="nextTip()"
                          ></Tooltip>
                        </div>
                      </v-expansion-panel-header>
                      <v-expansion-panel-content class="mt-6" v-if="myOrganisation">
                        <div v-if="myOrganisation.test_enabled">
                          <p>{{ $t('To demonstrate the system, a test tag has been generated') }}. {{ $t('You can scan this tag with the mobile app or press the button to simulate scanning the tag from this dashboard') }}.</p>
                          <p>{{ $t('A notification email will be sent to') }}: {{ adminUser.email }}</p>
                          <p>{{ $t('A SMS notification will be sent to') }}: {{ adminUser.mobile ? adminUser.mobile : 'Please add a mobile number to your account' }}</p>
                          <p>{{ $t('SMS text notifications remaining') }}: {{1000 - adminUser.sms_count}}</p>
                          <p>{{ $t('Scan tag code from mobile app') }}: {{ myOrganisation.test_qr_code }}</p>
                          <v-img
                              v-if="testImage"
                              class="white--text align-end mb-2"
                              width="200px"
                              height="200px"
                              :src="testImage"
                          >

                          </v-img>
                          <v-btn @click="testCodeSubmit">{{ $t('buttons.Simulate scan') }}</v-btn>
                        </div>
                        <v-card-text v-else>
                          <p>{{ $t('When we have enabled your organisation, a test tag will be generated for you here') }}.</p>
                        </v-card-text>
                      </v-expansion-panel-content>
                    </v-expansion-panel>



                    <v-expansion-panel
                        id="tooltip3"
                        class="mb-4"
                        style="border-radius: 10px !important;border-color: blue"
                    >
                      <v-expansion-panel-header>
                        <div class="d-flex justify-space-between">
                          <div class="text-body-1 font-weight-bold">
                            <v-badge
                              color="green"
                              :content="this.owners.length"
                              class="mr-12"
                              v-if="myOrganisation"
                            >
                              {{ $t('Members and tag owners of {organisation}', {organisation: myOrganisation.name}) }}
                            </v-badge>
                          </div>
                          <Tooltip
                              :title="$t('Members and tag owners')"
                              :content="$t('You can view all members with subscription status, and tag owners associated to your organisation here')"
                              :show="tooltips[2]"
                              :currentTooltip="currentTooltip"
                              :tooltipsLength="tooltipsCount"
                              v-on:end-tips="endTips()"
                              v-on:next-tip="nextTip()"
                          ></Tooltip>
                        </div>
                      </v-expansion-panel-header>
                      <v-expansion-panel-content class="mt-6">
<!--                        <p class="text-body-1">Learn more about how to earn commision from invited members <FaqTooltip id="1"></FaqTooltip></p>-->
                        <div>
                          <v-text-field
                              v-model="searchUsers"
                              append-icon="search"
                              :label="$t('labels.Search')"
                              single-line
                              hide-details
                          ></v-text-field>
                        </div>
                        <v-data-table
                            :headers="headers"
                            :items="getOwners"
                            :sort-by="['name']"
                            :items-per-page="5"
                            class="elevation-0"
                            :search="searchUsers"
                            loading="true"
                            loading-text="Loading"
                            item-key="key"
                        >
                          <template v-if="myOrganisation.digital_membership" v-slot:item.action="{ item }">
                            <v-switch v-model="item.tag_authorised" :label="$t('labels.Authorised')" @change="updateTag(item)"></v-switch>
                          </template>
                        </v-data-table>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                    <v-expansion-panel
                        id="tooltip4"
                        class="mb-4"
                        style="border-radius: 10px !important;border-color: blue"
                    >
                      <v-expansion-panel-header>
                        <div class="d-flex justify-space-between">
                          <div class="text-body-1 font-weight-bold">{{ $t('headings.Found / collected scan history') }}</div>
                          <Tooltip
                              :title="$t('headings.Found / collected scan history')"
                              :content="$t('To enable you to keep track of your lost property, the history of scanned and collected items will be shown here')"
                              :show="tooltips[3]"
                              :currentTooltip="currentTooltip"
                              :tooltipsLength="tooltipsCount"
                              v-on:end-tips="endTips()"
                              v-on:next-tip="nextTip()"
                          ></Tooltip>
                        </div>
                      </v-expansion-panel-header>
                      <v-expansion-panel-content class="mt-6">
                        <v-text-field
                            v-model="searchActivity"
                            append-icon="search"
                            :label="$t('labels.Search')"
                            single-line
                            hide-details
                        ></v-text-field>
                        <v-data-table
                            :headers="activityHeaders"
                            :items="getHistories"
                            :items-per-page="5"
                            class="elevation-0"
                            :search="searchActivity"
                            loading="true"
                            :loading-text="$t('Loading')"
                            item-key="key"
                        >
                        </v-data-table>
                      </v-expansion-panel-content>
                    </v-expansion-panel>

                    <v-expansion-panel
                        v-if="myOrganisation && myOrganisation.affiliate"
                        id="tooltip5"
                        class="mb-4"
                        style="border-radius: 10px !important;border-color: blue"
                    >
                      <v-expansion-panel-header>
                        <div class="d-flex justify-space-between">
                          <div class="text-body-1 font-weight-bold">
                            <v-badge
                                color="green"
                                :content="affiliatedPartners.length"
                                class="mr-12"
                            >
                              {{ $t('Sub partners') }}
                            </v-badge>
                          </div>
                          <Tooltip
                              :title="$t('Sub partners')"
                              :content="$t('This will show the sub partners signed up under your organisation')"
                              :show="tooltips[4]"
                              :currentTooltip="currentTooltip"
                              :tooltipsLength="tooltipsCount"
                              v-on:end-tips="endTips()"
                              v-on:next-tip="nextTip()"
                          ></Tooltip>
                        </div>
                      </v-expansion-panel-header>
                      <v-expansion-panel-content class="mt-6">
                        <div v-if="myOrganisation && myOrganisation.affiliate">
                          <v-text-field
                              v-model="searchAffiliated"
                              append-icon="search"
                              :label="$t('labels.Search')"
                              single-line
                              hide-details
                          ></v-text-field>
                          <v-data-table
                              :headers="affiliatedHeaders"
                              :items="affiliatedPartners"
                              :items-per-page="5"
                              class="elevation-0"
                              :search="searchAffiliated"
                              loading="true"
                              :loading-text="$t('Loading')"
                              item-key="key"
                          >
                            <template v-slot:item.action="{ item }">
                              <v-icon
                                  small
                                  class="mr-2"
                                  @click="viewAffiliated(item)"
                              >
                                visibility
                              </v-icon>

                            </template>
                          </v-data-table>
                          <v-fab-transition>
                            <v-tooltip top>
                              <template v-slot:activator="{ on }">
                                <v-btn color="green" small fab dark v-on="on" class="float-right" @click="affiliatesDialog = true">
                                  <v-icon dark>mdi-plus</v-icon>
                                </v-btn>
                              </template>
                              {{ $t('Invite partners to sign up as affiliates to your organisation') }}
                            </v-tooltip>

                          </v-fab-transition>
                          <v-dialog
                              v-model="affiliatedDialog"
                              max-width="600"
                          >
                            <v-card v-if="affiliatedPartner">
                              <v-card-title class="text-h4">
                                {{ affiliatedPartner.name }}
                              </v-card-title>

                              <v-card-text>
                                <p class="text-body-1">
                                  {{ $t('Members') }}: {{ affiliatedPartner.members }}<br>
                                  {{ $t('Paid subscriptions') }}: {{ affiliatedPartner.subscriptions }}
                                </p>
                                <h2 class="mb-2">{{ $t('Partner details') }}</h2>
                                <p class="text-body-1">
                                  {{ affiliatedPartner.name }}<br>
                                  {{ affiliatedPartner.address1 }}<br>
                                  {{ affiliatedPartner.address2 }}<br>
                                  {{ affiliatedPartner.town }}<br>
                                  {{ affiliatedPartner.county }}<br>
                                  {{ affiliatedPartner.postcode }}<br>
                                  {{ affiliatedPartner.country }}<br>
                                </p>

                                <p class="text-body-1">
                                  {{ $t('Phone') }}: {{ affiliatedPartner.phone }}<br>
                                  {{ $t('Category') }}: {{ affiliatedPartner.organisation_category.name }}
                                </p>

                                <h2 class="mb-2">{{ $t('headings.Administrator') }}</h2>
                                <p class="text-body-1">
                                  {{ $t('Name') }}: {{ affiliatedPartner.administrators[0].name }}<br>
                                  {{ $t('Email') }}: {{ affiliatedPartner.administrators[0].email }}
                                </p>
                              </v-card-text>

                              <v-card-actions>
                                <v-spacer></v-spacer>
                                <v-btn
                                    text
                                    @click="affiliatedDialog = false"
                                >
                                  {{ $t('buttons.Close') }}
                                </v-btn>
                              </v-card-actions>
                            </v-card>
                          </v-dialog>
                        </div>
                      </v-expansion-panel-content>
                    </v-expansion-panel>

                  </v-expansion-panels>
                </v-col>
              </v-row>

                <v-dialog v-model="organisationDialog" max-width="600px">
                    <v-card class="form-card">
                        <v-card-title>
                            <span class="headline">{{ $t('headings.Edit Organisation') }}</span>
                        </v-card-title>
                        <v-card-text>
                            <v-form v-if="myOrganisation" v-model="organisationValid" @submit.prevent="handleSubmitOrganisation">
                              <file-pond
                                  name="test"
                                  ref="addPond"
                                  class-name="my-pond"
                                  :label-idle="$t('Drop your organisation logo here') + '...'"
                                  allow-multiple="false"
                                  accepted-file-types="image/jpeg, image/png"
                                  v-bind:files="myFiles"
                                  v-on:init="handleFilePondInit"/>
                                <ValidationObserver ref="organisationObserver">
                                    <ValidationProvider vid="name" :name="$t('labels.Organisation name')" rules="required" v-slot="{ errors }">
                                        <v-text-field
                                                v-model="myOrganisation.name"
                                                :error-messages="errors[0]"
                                                :label="$t('labels.Organisation name')"
                                                required
                                        ></v-text-field>
                                    </ValidationProvider>
                                    <ValidationProvider vid="phone" :name="$t('labels.Organisation phone number')" rules="required" v-slot="{ errors }">
                                        <v-text-field
                                                v-model="myOrganisation.phone"
                                                :error-messages="errors[0]"
                                                :label="$t('labels.Organisation phone number')"
                                                required
                                                persistent-hint
                                                :hint="$t('hints.Include extension if needed')"
                                        ></v-text-field>
                                    </ValidationProvider>

                                    <ValidationProvider vid="category" :name="$t('labels.Organisation category')" rules="required" v-slot="{ errors }">
                                        <v-select
                                                :items="categoriesList"
                                                item-text="name"
                                                item-value="id"
                                                v-model="myOrganisation.organisation_category_id"
                                                :error-messages="errors[0]"
                                                :label="$t('labels.Organisation type')"
                                                required
                                        ></v-select>
                                    </ValidationProvider>

                                    <ValidationProvider v-if="myOrganisation && !myOrganisation.affiliate" vid="affiliate_partner" :name="$t('labels.Super partner')" v-slot="{ errors }">
                                        <v-autocomplete
                                                :items="affiliateOrganisations"
                                                item-text="name"
                                                item-value="id"
                                                v-model="myOrganisation.affiliate_partner"
                                                :error-messages="errors[0]"
                                                chips
                                                persistent-hint
                                                :hint="$t('hints.Selected partner can view your details')"
                                        >
                                            <template v-slot:label>
                                                <div>
                                                    {{ $t('Super partner associated with') }} <small>({{ $t('optional') }})</small>
                                                </div>
                                            </template>
                                        </v-autocomplete>
                                    </ValidationProvider>

                                    <ValidationProvider vid="postcode" :name="$t('labels.Postcode')" rules="required" v-slot="{ errors }">
                                        <v-text-field
                                                v-model="myOrganisation.postcode"
                                                :error-messages="errors[0]"
                                                :label="$t('labels.Postcode')"
                                                required
                                        ></v-text-field>
                                        <v-btn v-if="brand === 'findmykit'" @click="postcodeLookup" class="mb-6">{{ $t('buttons.Lookup address from UK postcode') }}</v-btn>
                                    </ValidationProvider>

                                    <v-select
                                            v-if="addresses.length > 0"
                                            :items="addresses"
                                            :label="$t('labels.Choose address')"
                                            v-on:change="setAddress"
                                            autofocus
                                    ></v-select>

                                    <ValidationProvider vid="address1" :name="$t('labels.Address line 1')" rules="required" v-slot="{ errors }">
                                        <v-text-field
                                                v-model="myOrganisation.address1"
                                                :error-messages="errors[0]"
                                                :label="$t('labels.Address line 1')"
                                                required
                                        ></v-text-field>
                                    </ValidationProvider>

                                    <ValidationProvider vid="address2" :name="$t('labels.Address line 2')" v-slot="{ errors }">
                                        <v-text-field
                                                v-model="myOrganisation.address2"
                                                :error-messages="errors[0]"
                                                :label="$t('labels.Address line 2')"
                                                required
                                        ></v-text-field>
                                    </ValidationProvider>

                                    <ValidationProvider vid="town" :name="$t('labels.Town')" rules="required" v-slot="{ errors }">
                                        <v-text-field
                                                v-model="myOrganisation.town"
                                                :error-messages="errors[0]"
                                                :label="$t('labels.Town')"
                                                required
                                        ></v-text-field>
                                    </ValidationProvider>

                                    <ValidationProvider vid="county" :name="$t('labels.County')" rules="required" v-slot="{ errors }">
                                        <v-text-field
                                                v-model="myOrganisation.county"
                                                :error-messages="errors[0]"
                                                :label="$t('labels.County')"
                                                required
                                        ></v-text-field>
                                    </ValidationProvider>

                                    <ValidationProvider vid="country" :name="$t('labels.Country')" rules="required" v-slot="{ errors }">
                                        <v-select
                                                :items="countriesList"
                                                item-text="name"
                                                item-value="code"
                                                v-model="myOrganisation.country"
                                                :error-messages="errors[0]"
                                                :label="$t('labels.Country')"
                                                required
                                        ></v-select>
                                    </ValidationProvider>

                                    <ValidationProvider vid="password" :name="$t('labels.Password')" rules="required" v-slot="{ errors }">
                                        <v-text-field
                                                ref="password"
                                                v-model="myOrganisation.password"
                                                :append-icon="showPassword ? 'visibility' : 'visibility_off'"
                                                :type="showPassword ? 'text' : 'password'"
                                                :error-messages="errors[0]"
                                                :label="$t('labels.Confirm password')"
                                                required
                                                autocapitalize="none"
                                                @click:append="showPassword = !showPassword"
                                        ></v-text-field>
                                    </ValidationProvider>

                                </ValidationObserver>

                            </v-form>
                        </v-card-text>
                        <v-card-actions class="d-flex justify-space-between">
                            <v-btn class="pa-2" color="blue darken-1" text @click="organisationDialog = false">{{ $t('buttons.Close') }}</v-btn>
                            <v-btn class="pa-2" rounded elevation="0" color="primary" @click="handleSubmitOrganisation">{{ $t('buttons.Submit') }}</v-btn>
                        </v-card-actions>
                    </v-card>
                </v-dialog>

                <v-row v-if="!adminForOrganisations[0].enabled">
                  <v-col
                      sm="12"
                      class="action-col"
                  >
                    <v-card elevation="0" class="form-card">
                      <v-card-title>
                        <p>{{ $t('When we have enabled your organisation you will be able to scan tags, and invite people to your organisation here') }}. {{ $t('You will receive an email from us when you are enabled') }}. {{ $t('You will need to logout and back in again to see the changes') }}.</p>
                      </v-card-title>
                    </v-card>
                  </v-col>
                </v-row>
            </v-container>
        </v-container>

        <Footer />

        <v-snackbar v-model="snack" :timeout="3000" :color="snackColor">
            {{ snackText }}
            <template v-slot:action="{ attrs }">
                <v-btn
                        text
                        v-bind="attrs"
                        @click="snack = false"
                >
                    {{ $t('buttons.Close') }}
                </v-btn>
            </template>
        </v-snackbar>

<!--      <v-menu-->
<!--          top-->
<!--          open-on-hover-->
<!--          transition="slide-y-transition"-->
<!--          :offset-y="offset"-->
<!--      >-->
<!--        <template v-slot:activator="{ on, attrs }">-->

<!--          <v-btn-->
<!--              class="help-menu"-->
<!--              color="primary"-->
<!--              dark-->
<!--              fab-->
<!--              bottom-->
<!--              right-->
<!--              v-bind="attrs"-->
<!--              v-on="on"-->
<!--          >-->
<!--            <v-icon light>-->
<!--              mdi-help-->
<!--            </v-icon>-->
<!--          </v-btn>-->
<!--        </template>-->

<!--        <v-list dense>-->
<!--          <v-list-item>-->
<!--            <v-list-item-title @click="navigate('faqs')">{{ $t('Frequently asked questions') }}</v-list-item-title>-->
<!--          </v-list-item>-->
<!--          <v-list-item>-->
<!--            <v-list-item-title @click="replayTips">{{ $t('headings.Replay tooltip tour') }}</v-list-item-title>-->
<!--          </v-list-item>-->
<!--        </v-list>-->
<!--      </v-menu>-->

      <v-dialog
          v-model="welcomeDialog"
          transition="dialog-top-transition"
          max-width="600"
      >
        <v-card class="welcome">
          <v-toolbar
              color="primary"
              dark
          ><div class="text-h4">{{ $t('headings.Welcome') }}</div></v-toolbar>
          <v-card-text>
            <p class="text-h5 pa-12 pb-0">{{ $t("Thank you for subscribing to {brand} and a very warm welcome to our community, the UK's revolutionary lost property solution!", {brand: brandName}) }}</p>
            <p class="text-h5 pa-12 pt-0 pb-0">{{ $t('Press continue to be guided through your dashboard, and the next steps to get you on board') }}.</p>
            <p class="text-h5 pa-12 pt-0 pb-0">{{ $t('If you prefer to speak to a human then you are always welcome to email our team on') }} <a :href="'mailto:' + supportEmail">{{supportEmail}}</a></p>
            <p class="text-h5 pa-12 pt-0 pb-0">{{ $t('Thanks, the {brand} team', {brand: brandName}) }}</p>
          </v-card-text>
          <v-card-actions class="justify-end">
            <v-btn
                color="primary"
                elevation="0"
                @click="closeWelcome"
            >{{ $t('buttons.Continue') }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog
          v-model="affiliateWelcomeDialog"
          transition="dialog-top-transition"
          max-width="600"
      >
        <v-card class="welcome">
          <v-toolbar
              color="primary"
              dark
          ><div class="text-h4">{{ $t('headings.You are now a super partner') }} <v-icon dark large>mdi-medal</v-icon></div></v-toolbar>
          <v-card-text>
            <p class="text-h5 pa-12 pb-0">{{ $t('You can now invite other organisations to become part of your {brand} community', {brand: brandName}) }}.</p>
            <p class="text-h5 pa-12 pt-0 pb-0">{{ $t('Press continue to be guided through your dashboard, to see the added functionality for a super partner') }}.</p>
            <p class="text-h5 pa-12 pt-0 pb-0">{{ $t('If you prefer to speak to a human then you are always welcome to email our team on') }} <a :href="'mailto:' + supportEmail">{{supportEmail}}</a></p>
            <p class="text-h5 pa-12 pt-0 pb-0">{{ $t('Thanks, the {brand} team', {brand: brandName}) }}</p>
          </v-card-text>
          <v-card-actions class="justify-end">
            <v-btn
                color="primary"
                elevation="0"
                @click="closeAffiliateWelcome"
            >{{ $t('buttons.Continue') }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog
          v-model="communityDialog"
          transition="dialog-top-transition"
          max-width="600"
          class="welcome"
      >
        <v-card class="welcome">
          <v-toolbar
              color="primary"
              dark
          ><div class="text-h4">{{ $t('headings.Build your community') }} <v-icon dark large>mdi-account-group</v-icon></div></v-toolbar>
          <v-card-text>
            <v-carousel light hide-delimiters :show-arrows="false" v-model="slides" class="mt-3"  :style="{ 'background-size': 'contain', 'background-position': 'center' }">
              <v-carousel-item>
                <v-sheet
                    height="100%"
                    rounded
                    style="background-color: transparent"
                >
                  <v-row
                      align="top"
                      justify="left"
                  >
                    <p class="text-h5 pa-10 pb-0 font-weight-bold">{{ $t('Building your community is an integral part of using the {brand} platform', {brand: brandName}) }}.</p>
                    <p class="text-h5 pa-10 pt-0 pb-0">{{ $t('Take the tour to learn about how we can help you, click next') }}.</p>
                  </v-row>
                </v-sheet>
              </v-carousel-item>
              <v-carousel-item v-if="myOrganisation && myOrganisation.affiliate">
                <v-sheet
                    height="100%"
                    rounded
                    style="background-color: transparent"
                >
                  <v-row
                      align="top"
                      justify="left"
                  >
                    <p class="text-h5 pa-10 pb-0 font-weight-bold">{{ $t('Inviting your sub partners') }}</p>
                    <p class="text-h5 pa-10 pt-0 pb-0">{{ $t('To help you grow your community, invite the people in your network of organisations and groups to become your sub partners and part of your extended {brand} family, all from your dashboard', {brand: brandName}) }}.</p>
                  </v-row>
                </v-sheet>
              </v-carousel-item>
              <v-carousel-item>
                <v-sheet
                    height="100%"
                    rounded
                    style="background-color: transparent"
                >
                  <v-row
                      align="top"
                      justify="left"
                  >
                    <p class="text-h5 pa-10 pb-0 font-weight-bold">{{ $t('Building your members community') }}</p>
                    <p class="text-h5 pa-10 pt-0 pb-0">{{ $t("We've made it easy to engage with potential new members and grow your community") }}. {{ $t('We have provided all the information you need in our downloadable marketing pack and we after our full support along the way') }}.</p>
                    <p class="text-h5 pa-10 pt-0 pb-0">{{ $t('Invite members via your dashboard and for every successful subscription you will receive commission') }}.</p>
                  </v-row>
                </v-sheet>
              </v-carousel-item>
              <v-carousel-item>
                <v-sheet
                    height="100%"
                    rounded
                    style="background-color: transparent"
                >
                    <p class="text-h5 pa-5 pb-0 font-weight-bold">{{ $t('Your landing page') }}</p>
                    <p class="text-h5 pa-5 pt-0 pb-0">{{ $t("Invited members will receive a link to your landing page, providing a call to action for your potential members to register under your organisation") }}.</p>
                    <p class="text-h5 pa-5 pt-0 pb-0"><a :href="shareLink">{{ shareLink }}</a></p>
                    <p class="text-h5 pa-5 pt-0 pb-0">{{ $t('Copy your landing page link') }}. <v-tooltip top content-class="info tooltip-right tooltip">
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn v-if="myOrganisation"
                               v-bind="attrs"
                               v-on="on"
                               class="align-self-center ml-2"
                               v-clipboard:copy="shareLink"
                               v-clipboard:success="onCopy"
                               v-clipboard:error="onError"
                               fab
                               small
                        ><v-icon>mdi-share-variant</v-icon></v-btn>
                      </template>
                  <div>{{ $t('Click to copy invite link for your organisation') }}. {{ $t('Paste this link in your own email to invite members') }}.</div>
                  </v-tooltip>
                  </p>

                      <p class="text-h5 pa-5 pt-0 pb-0">{{ $t('If you would like to customised your landing page please contact {support}', {support: supportEmail}) }}</p>

                </v-sheet>
              </v-carousel-item>
            </v-carousel>

          </v-card-text>
          <v-card-actions class="justify-end">
            <v-btn
                v-if="slides !== 0"
                color="primary"
                elevation="0"
                @click="slides--"
            >{{ $t('buttons.Previous') }}</v-btn>
            <v-btn
                v-if="myOrganisation && myOrganisation.affiliate && slides !== 3"
                color="primary"
                elevation="0"
                @click="slides++"
            >{{ $t('buttons.Next') }}</v-btn>
            <v-btn
                v-else-if="myOrganisation && myOrganisation.affiliate"
                color="primary"
                elevation="0"
                @click="closeCommunityDialog"
            >{{ $t('buttons.Finish') }}</v-btn>

            <v-btn
                v-if="myOrganisation && !myOrganisation.affiliate && slides !== 2"
                color="primary"
                elevation="0"
                @click="slides++"
            >{{ $t('buttons.Next') }}</v-btn>
            <v-btn
                v-else-if="myOrganisation && !myOrganisation.affiliate"
                color="primary"
                elevation="0"
                @click="closeCommunityDialog"
            >{{ $t('buttons.Finish') }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
</template>

<script>

    import { mapState, mapActions } from 'vuex'

    let hpBanner1 = require('@/assets/hp-banner-1.jpg')

    import { tagService, userService } from '../services';
    import axios from 'axios';

    import { store } from '../store'
    import Footer from './Footer.vue'
    import { inviteService } from "../services";

    import { extend } from 'vee-validate';
    import { required, min } from 'vee-validate/dist/rules';
    // Add the required rule
    extend('required', required);
    extend('min', min);

    // Import FilePond
    import vueFilePond from 'vue-filepond';

    // Import plugins
    import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type/dist/filepond-plugin-file-validate-type.esm.js';
    import FilePondPluginImagePreview from 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.esm.js';
    import FilePondPluginFileEncode from 'filepond-plugin-file-encode/dist/filepond-plugin-file-encode.esm.js';



    // Import styles
    import 'filepond/dist/filepond.min.css';
    import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.min.css';

    // Create FilePond component
    const FilePond = vueFilePond(
        FilePondPluginFileValidateType,
        FilePondPluginImagePreview,
        FilePondPluginFileEncode );

    import Tooltip from "./Tooltip";
    import FaqTooltip from "./FaqTooltip";

    import UseCSVButton from "@usecsv/vuejs";
    import {i18n} from "../i18n";

    export default {
        name: "Dashboard",
        components: {
            'Footer': Footer,
            FilePond,
            Tooltip,
            FaqTooltip,
            "usecsv-button": UseCSVButton
        },
        props: ['qrCode'],
        metaInfo () {
          return {
            title: this.capitalizeFirstLetter(this.brand) + ' - ' + i18n.t('headings.Dashboard'),
            htmlAttrs: {
              lang: i18n.locale
            }
          }
        },
        data: () => ({
            showMembers: false,
            showPartners: false,
            showMarketing: false,
            slides: 0,
            currentTooltip: 0,
            tooltips: [false,false,false,false,false,false,false,false,false],
            welcomeDialog: false,
            affiliateWelcomeDialog: false,
            offset: true,
            affiliatesDialog: false,
            openedPanel: null,
            myFiles: [],
            showPassword: false,
            snack: false,
            snackColor: '',
            snackText: '',
            organisationDialog: false,
            myOrganisation: null,
            organisationObserver: '',
            organisationValid: false,
            categories: [
                {name: 'Educational', id: 1},
                {name: 'Transport & travel', id: 2},
                {name: 'Sports and leisure', id: 3},
                {name: 'Retail', id: 4},
                {name: 'Other', id: 5},
                {name: 'Super partner', id: 6}
            ],
            showAddress: false,
            postcode: '',
            addresses: [],
            coordinates: {},
            hpBanner1: hpBanner1,
            searchUsers: '',
            searchActivity: '',
            code: '',
            tag: [
                {
                    owner_name: '',
                    users: [

                    ]
                }
            ],
            found: false,
            foundDialog: false,
            notified: null,
            item: '',
            organisation: '',
            note: '',
            pagination: {},
            headers: [
                {
                    text: i18n.t('Name'),
                    align: 'left',
                    sortable: false,
                    value: 'name'
                },
                {
                    text: i18n.t('Account holder'),
                    align: 'left',
                    sortable: false,
                    value: 'is_member'
                },
                {
                    text: i18n.t('Subscribed'),
                    align: 'left',
                    sortable: false,
                    value: 'membership'
                },
                {
                    text: i18n.t('On trial'),
                    align: 'left',
                    sortable: false,
                    value: 'on_trial'
                },
                { text: 'Actions', value: 'action', sortable: false }
            ],
            activityHeaders: [
                {
                    text: i18n.t('Scanned at'),
                    align: 'left',
                    sortable: false,
                    value: 'created_at'
                },
                {
                    text: i18n.t('Collected at'),
                    align: 'left',
                    sortable: false,
                    value: 'collected_at'
                },
                {
                    text: i18n.t('QR code'),
                    align: 'left',
                    sortable: false,
                    value: 'tag.qr_code'
                },
                {
                    text: i18n.t('Tag owner'),
                    align: 'left',
                    sortable: false,
                    value: 'tag.owner.name'
                },
                {
                    text: i18n.t('Item'),
                    align: 'left',
                    sortable: false,
                    value: 'item'
                }
            ],
            owners: [],
            histories: [],
            emails: [],
            organisationFrom: '',
            invited: false,
            search: null,
            chips: true,
            multiple: true,
            hideSelected: true,
            noData: true,
            clearable: false,
            usersDialog: false,
            dependantDialog: false,
            submittedDependant: false,
            activeOrganisations: [],
            editDialog: false,
            editedIndex: -1,
            editedItem: {
                name: '',
                relationship: '',
                organisations: null
            },
            name: '',
            relationship: '',
            organisations: '',
            membersTotal: 0,
            subscribersTotal: 0,
            onTrialTotal: 0,
            affiliatedHeaders: [
                {
                    text: 'Name',
                    align: 'left',
                    sortable: false,
                    value: 'name'
                },
                {
                  text: 'Members',
                  align: 'left',
                  sortable: false,
                  value: 'members'
                },
                {
                  text: 'Subscriptions',
                  align: 'left',
                  sortable: false,
                  value: 'subscriptions'
                },
                {
                    text: 'Created at',
                    align: 'left',
                    sortable: false,
                    value: 'created_at'
                },
                { text: 'Actions', value: 'action', sortable: false }
            ],
            searchAffiliated: '',
            affiliatedDialog: false,
            affiliatedPartner: null,
            affiliateOrganisations: [],
            communityDialog: false,
            earnedTotal: 0
        }),
        watch: {
          usersDialog(val) {
            if (val) {
              if (this.$refs.inviteObserver) this.$refs.inviteObserver.reset()
              this.invited = false;
              this.emails = [];
              this.organisationFrom = '';
            }
          },
          affiliatesDialog(val) {
            if (val) {
              if (this.$refs.invitePartnersObserver) this.$refs.invitePartnersObserver.reset()
              this.invited = false;
              this.emails = [];
              this.organisationFrom = '';
            }
          },
          welcomeDialog(val) {
            if (val === false) {
              this.replayTips()
            }
          },
          communityDialog(val) {
            if (val === false) {
              setTimeout(() => {
                this.slides = 0
              }, 2000)
            }
          },
        },
        computed: {
            ...mapState({
                adminForOrganisations: state => state.account.user.admin_for_organisations,
                dependants: state => state.account.user.dependants,
                adminUser: state => state.account.user,
                tooltipsComplete: state => state.account.user.tooltips_complete,
                welcomeShown: state => state.account.user.welcome_shown,
                affiliateWelcomeShown: state => state.account.user.affiliate_welcome_shown,
                testscanComplete: state => state.account.user.testscan_complete,
                membersInvited: state => state.account.user.members_invited,
                partnersInvited: state => state.account.user.partners_invited,
                brand: state => state.brand,
                brandName: state => state.brandName
            }),
            supportEmail() {
              if (this.brand === 'neotag') {
                return 'info@neotag.com'
              }
              else {
                return 'support@findmykit.com'
              }
            },
            getItems() {
              return [
                i18n.t('Football/Cricket Boots'),
                i18n.t('Trainers'),
                i18n.t('PE Kit'),
                i18n.t('Hoodie'),
                i18n.t('Trousers'),
                i18n.t('Shirt'),
                i18n.t('House Top'),
                i18n.t('Blazer'),
                i18n.t('Bag'),
                i18n.t('Hat'),
                i18n.t('Gloves(s)'),
                i18n.t('Shoe(s)'),
                i18n.t('Folder/Book'),
                i18n.t('Keys'),
                i18n.t('Stationary'),
                i18n.t('Coat'),
                i18n.t('Phone/Tablet'),
                i18n.t('Computer'),
                i18n.t('Sports Kit'),
                i18n.t('Sock(s)'),
                i18n.t('Shorts/Skort'),
                i18n.t('Lunchbox'),
                i18n.t('Water Bottle'),
                i18n.t('Jumper/Cardigan'),
                i18n.t('Tie'),
                i18n.t('Dress/Kilt'),
                i18n.t('Leotard'),
                i18n.t('Swimwear'),
                i18n.t('Shirt/Blouse'),
                i18n.t('Travel Card/Pass'),
                i18n.t('Cricket Whites'),
                i18n.t('Hocky Stick/Cricket Bat'),
                i18n.t('Shin-pads'),
                i18n.t('Tracksuit'),
                i18n.t('Apron'),
                i18n.t('Leggings'),
                i18n.t('Branded Fleece'),
                i18n.t('Other')
              ]
            },
            hasMembers() {
              return this.membersInvited || this.membersTotal > 0;
            },
            hasPartners() {
                return this.partnersInvited || this.affiliatedPartners.length > 0
            },
            getImporterKey() {
              return process.env.VUE_APP_USE_CSV_KEY
            },
            tooltipsCount() {
              if (this.myOrganisation && this.myOrganisation.affiliate) {
                return this.tooltips.length
              }
              else {
                return this.tooltips.length - 2
              }
            },
            strength() {
              let value = 33
              if (this.myOrganisation && this.myOrganisation.affiliate) {
                value = 25
              }
              console.log(value)

              if (this.testscanComplete) {
                if (this.myOrganisation && this.myOrganisation.affiliate) {
                  value += 25
                }
                else {
                  value += 33
                }
                console.log(value)
              }

              if (this.membersInvited || this.membersTotal > 0) {
                if (this.myOrganisation && this.myOrganisation.affiliate) {
                  value += 25
                }
                else {
                  value += 33
                }
                console.log("members")
              }

              if (this.myOrganisation && this.myOrganisation.affiliate && (this.partnersInvited || this.affiliatedPartners.length > 0)) {
                value += 25
                console.log(value)
              }

              if (value === 99) {
                value = 100
              }

              return value
            },
            userIsOrganisation() {
                return store.getters.userHasRole(['organisation'])
            },
            tagDetails () {
                console.log(this.tag)
                return this.tag
            },
            organisationRole () {
                return store.getters['account/isAdmin']
            },
            dashboardHeading () {
                return store.getters['account/organisationName']
            },
            categoriesList () {
                // return [{name: 'Educational', id: 1},{name: 'Transport & travel', id: 2}]
                return this.categories
            },
            countriesList () {
              return [
                {name: i18n.t('United Kingdom'), code: 'GB'},
                {name: i18n.t('Singapore'), code: 'SG'},
                {name: i18n.t('Spain'), code: 'ES'},
              ]
            },
            testImage () {
                if (this.myOrganisation.test_image) {
                    return 'data:image/png;base64,' + this.myOrganisation.test_image
                }
                else {
                    return null
                }
            },
            shareLink () {
                return 'https://findmykit.com/sign-up/' + this.myOrganisation.slug
            },
            shareLinkAffiliate () {
                return 'https://findmykit.com/sign-up-organisation?affiliate=' + this.myOrganisation.slug
            },
            affiliatedPartners () {
                if (this.myOrganisation && this.myOrganisation.affiliated_partners && this.myOrganisation.affiliated_partners.length > 0) {
                    return this.myOrganisation.affiliated_partners.map((item, index) => ({
                        key: 'affiliated_partners_' + index,
                        ...item
                    }))
                }

                return []
            },
            getHistories () {
                if (this.histories && this.histories.length > 0) {
                    return this.histories.map((item, index) => ({
                        key: 'history_' + index,
                        ...item
                    }))
                }

                return []
            },
            getOwners () {
                if (this.owners && this.owners.length > 0) {
                    return this.owners.map((item, index) => ({
                        key: 'owners_' + index,
                        ...item
                    }))
                }

                return []
            },
        },
        methods: {
            ...mapActions('account', ['register', 'addDependant', 'updateDependant', 'updateOrganisation', 'completeTooltips', 'completeWelcome', 'completeAffiliateWelcome', 'completeTestscan', 'completeInviteMembers', 'completeInvitePartners']),
            updateTag(item) {

              Object.keys(item.tags).forEach(key => {

                if (item.tags[key]['personal']) {
                  let tag = item.tags[key]

                  const instance = axios.create({
                    baseURL: process.env.VUE_APP_NEOTAG_HOSTNAMES.split(",").includes(window.location.hostname) ? process.env.VUE_APP_API_HOST_NEOTAG : process.env.VUE_APP_API_HOST,
                    withCredentials: true,
                  });

                  instance
                      .patch('/api/tags/' + tag.id + '/update-authorised', {
                        authorised: item.tag_authorised
                      })
                      .then(response => {
                        console.log(response.data.data)

                      })
                      .catch(error => {
                        console.log(error)
                        if (error.response && [401, 403, 419].includes(error.response.status)) {
                          userService.logout().then(() => {
                            store.dispatch('alert/error', i18n.t('You have been logged out due to inactivity'), { root: true });
                          })
                        }
                      })
                      .finally(() => this.show = true)
                }
              })
            },
            capitalizeFirstLetter(string) {
              return string.charAt(0).toUpperCase() + string.slice(1);
            },
            onData: function (data) {
              for (let i = 0; i < data.rows.length; i++) {
                this.emails.push(data.rows[i].email)
              }
            },
            navigate(routeName) {
              this.$router.push({name: routeName})
            },
            closeCommunityDialog() {
              this.communityDialog = false
            },
            closeWelcome() {
              this.welcomeDialog = false
              this.completeWelcome()
            },
            closeAffiliateWelcome() {
              this.affiliateWelcomeDialog = false
              this.completeAffiliateWelcome()
            },
            nextTip() {

              // setTimeout(function() {
              //   let target = document.querySelector('#' + hash);
              //   console.log(target.offsetTop)
              //   document.getElementById("scrolling-content").scrollTop = target.offsetTop +120
              // }, 1000);

              //window.scrollTo(0,0);

              this.tooltips = [false,false,false,false,false,false,false,false,false]

              this.openedPanel = null
              let nextPanel = null

              if (this.currentTooltip === 1) {
                nextPanel = 0
              }
              else if (this.currentTooltip === 2) {
                nextPanel = 1
              }
              else if (this.currentTooltip === 3) {
                nextPanel = 2
              }
              else if (this.currentTooltip === 4) {
                nextPanel = 3
              }

              console.log("currentTooltip: " + this.currentTooltip)

              let scrolledTo = document.getElementById("scrolling-content").scrollTop

              setTimeout(() => {
                if (this.currentTooltip === 1) {

                  let target = document.querySelector("#tooltip2");
                  console.log(target.getBoundingClientRect().top)
                  let offset = target.getBoundingClientRect().top + scrolledTo
                  document.getElementById("scrolling-content").scrollTop = offset -180
                  setTimeout(() => {
                    this.tooltips = [false,true,false,false,false,false,false,false,false]
                    this.currentTooltip = 2
                    console.log("currentTooltip: " + this.currentTooltip)
                  }, 1000)
                }
                else if (this.currentTooltip === 2) {

                  let target = document.querySelector("#tooltip3");
                  console.log(target.getBoundingClientRect().top)
                  let offset = target.getBoundingClientRect().top + scrolledTo
                  document.getElementById("scrolling-content").scrollTop = offset -180
                  setTimeout(() => {
                    this.tooltips = [false,false,true,false,false,false,false,false,false]
                    this.currentTooltip = 3
                    console.log("currentTooltip: " + this.currentTooltip)
                  }, 1000)
                }
                else if (this.currentTooltip === 3) {
                  let target = document.querySelector("#tooltip4");
                  console.log(target.getBoundingClientRect().top)
                  let offset = target.getBoundingClientRect().top + scrolledTo
                  document.getElementById("scrolling-content").scrollTop = offset -180
                  setTimeout(() => {
                    this.tooltips = [false,false,false,true,false,false,false,false,false]
                    this.currentTooltip = 4
                    console.log("currentTooltip: " + this.currentTooltip)
                  }, 1000)
                }
                else if (this.currentTooltip === 4) {
                  let next = 5
                  let tips = [false,false,false,false,true,false,false,false,false]
                  if (!this.myOrganisation.affiliate) {
                    next = 6
                    tips = [false,false,false,false,false,true,false,false,false]
                  }
                  let target = document.querySelector("#tooltip" + next);
                  console.log(target.getBoundingClientRect().top)
                  let offset = target.getBoundingClientRect().top + scrolledTo
                  document.getElementById("scrolling-content").scrollTop = offset -180
                  setTimeout(() => {
                    this.tooltips = tips
                    console.log(tips)
                    this.currentTooltip = next
                    console.log("currentTooltip: " + this.currentTooltip)
                  }, 1000)
                }
                else if (this.currentTooltip === 5) {
                  console.log("Here")
                  let target = document.querySelector("#tooltip6");
                  console.log(target.getBoundingClientRect().top)
                  let offset = target.getBoundingClientRect().top + scrolledTo
                  document.getElementById("scrolling-content").scrollTop = offset -180
                  setTimeout(() => {
                    this.tooltips = [false,false,false,false,false,true,false,false,false]
                    this.currentTooltip = 6
                    console.log("Boo")
                    console.log("currentTooltip: " + this.currentTooltip)
                  }, 1000)

                }
                else if (this.currentTooltip === 6) {
                  let target = document.querySelector("#tooltip7");
                  let offset = target.getBoundingClientRect().top + scrolledTo
                  document.getElementById("scrolling-content").scrollTop = offset -180
                  setTimeout(() => {
                    this.tooltips = [false,false,false,false,false,false,true,false,false]
                    this.currentTooltip = 7
                    console.log("currentTooltip: " + this.currentTooltip)
                  }, 1000)
                }
                else if (this.currentTooltip === 7) {
                  let next = 8
                  let tips = [false,false,false,false,false,false,false,true,false]
                  if (!this.myOrganisation.affiliate) {
                    next = 9
                    tips = [false,false,false,false,false,false,false,false,true]
                  }
                  let target = document.querySelector("#tooltip" + next);
                  let offset = target.getBoundingClientRect().top + scrolledTo
                  document.getElementById("scrolling-content").scrollTop = offset -180
                  setTimeout(() => {
                    this.tooltips = tips
                    this.currentTooltip = next
                    console.log("currentTooltip: " + this.currentTooltip)
                  }, 1000)
                }
                else if (this.currentTooltip === 8) {

                  let target = document.querySelector("#tooltip9");
                  let offset = target.getBoundingClientRect().top + scrolledTo
                  document.getElementById("scrolling-content").scrollTop = offset -180
                  setTimeout(() => {
                    this.tooltips = [false,false,false,false,false,false,false,false,true]
                    this.currentTooltip = 9
                    console.log("currentTooltip: " + this.currentTooltip)
                  }, 1000)
                }
              }, 1000)


              console.log("openedPanel: " + nextPanel)

              this.openedPanel = nextPanel
            },
            endTips() {
              this.tooltips = [false,false,false,false,false,false,false,false,false]
              this.openedPanel = null

              this.completeTooltips()

              setTimeout(() => {
                this.currentTooltip = 0
              }, 5000)
            },
            replayTips() {
              window.scrollTo(0,0);

              this.currentTooltip = 1
              this.openedPanel = null
              this.tooltips = [false,false,false,false,false,false,false,false,false]
              setTimeout(() => {
                this.tooltips = [true,false,false,false,false,false,false,false,false]
              }, 600)
            },
            handleFilePondInit: function() {
              console.log('FilePond has initialized');

              // example of instance method call on pond reference
              // if (this.$refs.pond) {
              //   this.$refs.pond.removeFile();
              // }
              // if (this.$refs.addPond) {
              //   this.$refs.addPond.removeFile();
              // }
            },
            downloadMediaPack () {
              window.open('https://docs.google.com/presentation/d/e/2PACX-1vRdaB1NGekrIXbHnfAOpxIBCklA3eXRQkDtVdmy_1A8tlwFYIz3f2BNAlJy4O_azGaXHn2GmM23l-pa/pub?start=false&loop=false&delayms=3000')
            },
            onCopy: function (e) {
                this.snack = true
                this.snackColor = 'success'
                this.snackText = i18n.t('Invite link copied')
            },
            onError: function (e) {
                this.snack = true
                this.snackColor = 'error'
                this.snackText = i18n.t('Failed to copy link')
            },
            handleSubmitDependant () {

                let observer = this.$refs.addDependantObserver

                observer.validate().then(success => {
                    if (!success) {
                        return;
                    }

                    const { name, relationship, organisations } = this;
                    if (name, relationship) {

                        this.addDependant({ name, relationship, organisations }).then(dependant => {

                            if (!dependant) {
                                // Add the server side errors for each field.
                                observer.setErrors(store.getters.validationErrors);
                            }
                            else {
                                // Wait until the models are updated in the UI
                                this.$nextTick(() => {

                                    this.name = ''
                                    this.relationship = ''
                                    this.organisations = ''

                                    // remove errors
                                    observer.reset();

                                    this.dependantDialog = false
                                });
                            }
                        })
                    }
                });
            },
            handleSubmitOrganisation() {

                this.$refs.organisationObserver.validate().then(success => {

                    const { id, name, phone, organisation_category_id, affiliate_partner, postcode, address1, address2, town, county, country, latitude, longitude, password } = this.myOrganisation;

                    if (id && name && phone && organisation_category_id && postcode && address1 && town && county && country && password) {

                        let file = this.$refs.addPond.getFile(0)
                        let picture = null

                        if (file) {
                            picture = {
                                src: null,
                                title: null
                            }
                            picture.src = file.getFileEncodeBase64String()
                            picture.title = file.filename
                        }

                        this.updateOrganisation({id, name, phone, organisation_category_id, affiliate_partner, postcode, address1, address2, town, county, country, latitude, longitude, password, picture}).then(organisation => {
                            if (!organisation) {
                                // Add the server side errors for each field.
                                this.$refs.organisationObserver.setErrors(store.getters.validationErrors);

                            }
                            else {
                                this.myOrganisation = organisation
                                this.myOrganisation.password = null
                                this.organisationDialog = false
                            }
                        })
                    }
                }).catch(error => {
                    console.log(error)
                })
            },
            editOrganisation() {

                this.organisationDialog = true

                let organisationId = this.adminForOrganisations[0].id

                let apiClient = axios.create({
                    baseURL: process.env.VUE_APP_NEOTAG_HOSTNAMES.split(",").includes(window.location.hostname) ? process.env.VUE_APP_API_HOST_NEOTAG : process.env.VUE_APP_API_HOST,
                    withCredentials: true,
                });

                apiClient
                    .get('/api/organisations/' + organisationId)
                    .then(response => {
                        this.myOrganisation = response.data.data
                    })
                    .catch(error => {
                        console.log(error)
                        if (error.response && [401, 403, 419].includes(error.response.status)) {
                            userService.logout().then(() => {
                                store.dispatch('alert/error', i18n.t('You have been logged out due to inactivity'), { root: true });
                            })
                        }
                    })
            },
            editItem (item) {
                console.log(item)
                this.editedIndex = this.dependants.indexOf(item)
                this.editedItem = Object.assign({}, item)
                this.editDialog = true
            },

            // deleteItem (item) {
            //     const index = this.dependants.indexOf(item)
            //     confirm('Are you sure you want to delete this item?') && this.dependants.splice(index, 1)
            // },

            close () {
                console.log("close")
                this.editDialog = false
                setTimeout(() => {
                    this.editedItem = Object.assign({}, this.defaultItem)
                    this.editedIndex = -1
                }, 300)
            },
            save () {

                if (this.editedIndex > -1) {

                    let observer = this.$refs.editDependantObserver

                    observer.validate().then(success => {
                        if (!success) {
                            return;
                        }

                        let localEditedItem = this.editedItem

                        this.updateDependant({ localEditedItem }).then(dependant => {

                            if (!dependant) {
                                // Add the server side errors for each field.
                                observer.setErrors(store.getters.validationErrors);
                            }
                            else {
                                this.close()
                            }


                        })
                    })

                } else {
                    this.dependants.push(this.editedItem)
                }
                this.close()
            },
            handleSubmit () {

                const { code } = this;

                let codeString = code;

                // trim off trailing slash from older urls
                let lastChar = codeString.charAt(codeString.length - 1)

                if (lastChar === '/') {
                    codeString = codeString.slice(0, -1);
                }

                let n = codeString.lastIndexOf('/');
                if (n >= 0) {
                    codeString = codeString.substring(n + 1);
                    this.code = codeString;
                }


                let observer = this.$refs.scanObserver;

                observer.validate().then(success => {

                    if (!success) {
                        return;
                    }

                    tagService.find(codeString).then(response => {
                        this.tag = response.data;
                        this.notified = false;
                        this.found = true;
                        this.foundDialog = true;
                    }).catch(function (error) {
                        if (error.response && error.response.data && error.response.data.error) {
                            store.dispatch('alert/error', error.response.data.error, {root: true});
                        }
                        observer.setErrors(store.getters.validationErrors);
                    });

                })


                // if (tag) {
                //     router.push('scanned/' + tag)
                // }
            },
            testCodeSubmit() {
                this.code = this.myOrganisation.test_qr_code

                tagService.find(this.myOrganisation.test_qr_code).then(response => {
                  console.log(response.data)
                    this.tag = response.data;
                    this.notified = false;
                    this.found = true;
                    this.foundDialog = true;
                }).catch(function (error) {
                    if (error.response && error.response.data && error.response.data.error) {
                        store.dispatch('alert/error', error.response.data.error, {root: true});
                    }
                });

            },
            handleSubmitInvite () {

                let observer = this.$refs.inviteObserver;

                observer.validate().then(success => {
                    if (!success) {
                        return;
                    }

                    inviteService.invite(this.emails, this.myOrganisation.id).then(response => {
                        console.log(response)
                        this.usersDialog = false
                        this.snack = true
                        this.snackColor = 'success'
                        this.snackText = 'Invites sent'
                        this.completeInviteMembers()
                    }).catch(function (error) {
                        console.log(error)
                    });
                })
            },
            handleSubmitInvitePartners () {

              let observer = this.$refs.invitePartnersObserver;

              observer.validate().then(success => {
                if (!success) {
                  return;
                }

                inviteService.invitePartners(this.emails, this.myOrganisation.id).then(response => {
                  console.log(response)
                  this.affiliatesDialog = false
                  this.snack = true
                  this.snackColor = 'success'
                  this.snackText = 'Invites sent'
                  this.completeInvitePartners()
                }).catch(function (error) {
                  console.log(error)
                });
              })
            },
            handleNotify () {

                let observer = this.$refs.notifyObserver;

                let that = this

                observer.validate().then(success => {
                    if (!success) {
                        return;
                    }
                    tagService.notify(this.code, this.item, this.myOrganisation.id, this.note).then(() => {
                        this.found = false
                        this.code = ''
                        this.item = ''
                        //this.organisation = ''
                        this.note = ''
                        this.notified = 'Notification sent'

                        this.refreshScanHistory()
                        this.completeTestscan()
                    }).catch(function (error) {
                        console.log(error)
                        that.found = false
                        that.code = ''
                        that.item = ''
                        //that.organisation = ''
                        that.note = ''
                        that.notified = i18n.t('The account holder does not have an active subscription') + '. ' + i18n.t('They have not been notified on where to pick up this item') + '. ' + i18n.t('However, they will have received a notification to ask them to re-subscribe') + '.'
                    });
                })
            },
            handleCloseInvite () {
                this.usersDialog = false;
                this.invited = false;
                this.emails = [];
                this.organisationFrom = '';
            },
            postcodeLookup () {
                const { postcode } = this.myOrganisation;

                if (postcode) {

                    this.addresses = []

                    axios
                        .get('https://api.getAddress.io/find/' + postcode + '?api-key=' + process.env.VUE_APP_GET_ADDRESS_API_KEY)
                        .then(response => {
                            console.log(response.data)
                            this.addresses = response.data.addresses
                            this.coordinates = {
                                'latitude': response.data.latitude,
                                'longitude': response.data.longitude
                            }
                            //this.showAddress = true
                        })
                        .catch(error => {
                            console.log(error)
                        })
                }
            },
            setAddress (a) {
                console.log(a)
                this.addresses = []
                let address = a.split(",");
                this.myOrganisation.address1 = address[0]
                this.myOrganisation.address2 = address[1]
                this.myOrganisation.town = address[5]
                this.myOrganisation.county = address[6]
                this.myOrganisation.country = 'GB'
                this.myOrganisation.latitude = this.coordinates.latitude
                this.myOrganisation.longitude = this.coordinates.longitude
            },
            refreshScanHistory () {

                let apiClient = axios.create({
                    baseURL: process.env.VUE_APP_NEOTAG_HOSTNAMES.split(",").includes(window.location.hostname) ? process.env.VUE_APP_API_HOST_NEOTAG : process.env.VUE_APP_API_HOST,
                    withCredentials: true,
                });

                apiClient
                    .get('/api/history')
                    .then(response => {
                        this.histories = response.data.data
                        //console.log(this.histories)
                    })
                    .catch(error => {
                        console.log(error)
                    })
            },
            viewAffiliated (item) {

                let apiClient = axios.create({
                    baseURL: process.env.VUE_APP_NEOTAG_HOSTNAMES.split(",").includes(window.location.hostname) ? process.env.VUE_APP_API_HOST_NEOTAG : process.env.VUE_APP_API_HOST,
                    withCredentials: true,
                });

                apiClient
                    .get('/api/organisations/' + item.id)
                    .then(response => {
                        this.affiliatedPartner = response.data.data
                    })
                    .catch(error => {
                        console.log(error)
                        if (error.response && [401, 403, 419].includes(error.response.status)) {
                            userService.logout().then(() => {
                                store.dispatch('alert/error', i18n.t('You have been logged out due to inactivity'), { root: true });
                            })
                        }
                    })


                this.affiliatedDialog = true
            }
        },
        mounted () {

            if (this.qrCode) {
                this.code = this.qrCode
            }

            window.scrollTo(0,0);

            this.myOrganisation = this.adminForOrganisations[0]

            if (this.myOrganisation && this.myOrganisation.affiliate && this.affiliateWelcomeShown !== true) {
              this.affiliateWelcomeDialog = true
            }
            else if (this.welcomeShown !== true) {
              this.welcomeDialog = true
            }
            else if (this.tooltipsComplete !== true) {
              this.replayTips()
            }

            //userService.getDependants()

            const instance = axios.create({
              baseURL: process.env.VUE_APP_NEOTAG_HOSTNAMES.split(",").includes(window.location.hostname) ? process.env.VUE_APP_API_HOST_NEOTAG : process.env.VUE_APP_API_HOST,
            });

            instance
                .get('/api/organisations/enabled?brand=' + this.brand)
                .then(response => {
                    if (response.status === 200) {
                        this.activeOrganisations = response.data.data
                    }
                })
                .catch(error => {
                    console.log(error)
                })

            let apiClient = axios.create({
                baseURL: process.env.VUE_APP_NEOTAG_HOSTNAMES.split(",").includes(window.location.hostname) ? process.env.VUE_APP_API_HOST_NEOTAG : process.env.VUE_APP_API_HOST,
                withCredentials: true,
            });

            apiClient
                .get('/api/users')
                .then(response => {
                    this.owners = [...this.owners, ...response.data.data]

                    this.membersTotal = response.data.data.length

                    for(let x in this.owners) {
                        if (this.owners[x].membership) {
                            this.subscribersTotal++
                        }

                      if (this.owners[x].membership || this.owners[x].membership_cancelled) {
                        this.earnedTotal += 5
                      }

                        if (this.owners[x].on_trial) {
                            this.onTrialTotal++
                        }
                    }
                })
                .catch(error => {
                    console.log(error)
                    if (error.response && [401, 403, 419].includes(error.response.status)) {
                        userService.logout().then(() => {
                            store.dispatch('alert/error', i18n.t('You have been logged out due to inactivity'), { root: true });
                        })
                    }
                })

            apiClient
                .get('/api/dependants/by-organisations')
                .then(response => {
                    this.owners = [...this.owners, ...response.data.data]
                    //console.log(this.owners)
                })
                .catch(error => {
                    console.log(error)
                    if (error.response && [401, 403, 419].includes(error.response.status)) {
                        userService.logout().then(() => {
                            store.dispatch('alert/error', i18n.t('You have been logged out due to inactivity'), { root: true });
                        })
                    }
                })

            apiClient
                .get('/api/history')
                .then(response => {
                    this.histories = response.data.data
                    //console.log(this.histories)
                })
                .catch(error => {
                    console.log(error)
                    if (error.response && [401, 403, 419].includes(error.response.status)) {
                        userService.logout().then(() => {
                            store.dispatch('alert/error', i18n.t('You have been logged out due to inactivity'), { root: true });
                        })
                    }
                })

            let organisationId = this.adminForOrganisations[0].id

            apiClient
                .get('/api/organisations/' + organisationId)
                .then(response => {
                    this.myOrganisation = response.data.data
                })
                .catch(error => {
                    console.log(error)
                    if (error.response && [401, 403, 419].includes(error.response.status)) {
                        userService.logout().then(() => {
                            store.dispatch('alert/error', i18n.t('You have been logged out due to inactivity'), { root: true });
                        })
                    }
                })

            apiClient
                .get('/api/organisations/affiliates')
                .then(response => {
                    if (response.status === 200) {
                        this.affiliateOrganisations = response.data.data
                    }
                })
                .catch(error => {
                    console.log(error)
                })
        }
    }
</script>

<style>

</style>

<style scoped>
    .action-col {
        min-height: 165px;
    }
    .v-application--is-ltr .v-input__append-outer {
      margin-left: 0 !important
    }
    .theme--light.v-text-field--solo .v-input__control .v-input__slot {
      border-radius: 10px 0 0 10px !important;
    }
    .theme--dark.v-text-field--solo .v-input__control .v-input__slot {
      border-radius: 10px 0 0 10px !important;
    }
    .scan-button {
        height: 56px !important;
        border-radius: 0 10px 10px 0 !important;
        display: inline-block !important;
        box-shadow: none;
        background-color: var(--v-primary-base) !important;
        color: #fff !important;
        text-transform: none;
        margin-top: -12px;
        margin-left: -3px;
    }
    .invite-button.v-btn {
        border-radius: 10px;
        height: 56px !important;
        padding-left: 50px !important;
        padding-right: 50px !important;
        text-transform: none !important;
    }
    .download-button.v-btn {
      border-radius: 10px;
      height: 56px !important;
      padding-left: 20px !important;
      padding-right: 20px !important;
      text-transform: none !important;
    }
    .v-application .scan-form {
        display: inline-block;
    }
    .v-application .scan-form .error--text {
        color: #fff !important
    }
    .image-container {
        border-radius: 15px;
    }
    .tooltip-top::before {
      border-right: solid 8px transparent;
      border-left: solid 8px transparent;
      transform: translateX(-50%);
      position: absolute;
      z-index: -21;
      content: '';
      top: 100%;
      left: 50%;
      height: 0;
      width: 0;
    }
    .tooltip-top.error::before{
      border-top: solid 8px #ff6060;
    }

    .tooltip-top.success::before{
      border-top: solid 8px #1B5E20;
    }
    .tooltip-top.secondary::before{
      border-top: solid 8px #3f3f3f;
    }

    .tooltip-top.primary::before{
      border-top: solid 8px #246fb3;
    }

    .tooltip-top.info::before{
      border-top: solid 8px #1B1464;
    }


    .tooltip-bottom::before{
      border-right: solid 8px transparent;
      border-left: solid 8px transparent;
      transform: translateX(-50%);
      position: absolute;
      z-index: -21;
      content: '';
      bottom: 100%;
      left: 50%;
      height: 0;
      width: 0;
    }

    .tooltip-bottom.error::before{
      border-bottom: solid 8px #ff6060;
    }

    .tooltip-bottom.success::before{
      border-bottom: solid 8px #1B5E20;
    }
    .tooltip-bottom.secondary::before{
      border-bottom: solid 8px #3f3f3f;
    }

    .tooltip-bottom.primary::before{
      border-bottom: solid 8px #246fb3;
    }

    .tooltip-bottom.info::before{
      border-bottom: solid 8px #1B1464;
    }


    .tooltip-right::before{
      content: " ";
      position: absolute;
      top: 50%;
      right: 100%; /* To the left of the tooltip */
      margin-top: -8px;
      border-width: 8px;
      border-style: solid;
      border-top: solid 8px transparent;
      border-bottom: solid 8px transparent;
      border-left: solid 8px transparent;
    }

    .tooltip-right.error::before{
      border-right: solid 8px #ff6060 !important;
    }

    .tooltip-right.success::before{
      border-right: solid 8px #1B5E20 !important;
    }
    .tooltip-right.secondary::before{
      border-right: solid 8px #3f3f3f !important;
    }

    .tooltip-right.primary::before{
      border-right: solid 8px #246fb3 !important;
    }

    .tooltip-right.info::before{
      border-right: solid 8px #1B1464 !important;
    }



    .tooltip-left::before{
      content: " ";
      position: absolute;
      top: 50%;
      left: 100%; /* To the left of the tooltip */
      margin-top: -8px;
      border-width: 8px;
      border-style: solid;
      border-top: solid 8px transparent;
      border-bottom: solid 8px transparent;
      border-right: solid 8px transparent;
    }

    .tooltip-left.error::before{
      border-left: solid 8px #ff6060 !important;
    }

    .tooltip-left.success::before{
      border-left: solid 8px #1B5E20 !important;
    }
    .tooltip-left.secondary::before{
      border-left: solid 8px #3f3f3f !important;
    }

    .tooltip-left.primary::before{
      border-left: solid 8px #246fb3 !important;
    }

    .tooltip-left.info::before{
      border-left: solid 8px #1B1464 !important;
    }

    .v-tooltip__content {
      pointer-events: all !important;
    }

    .help-menu {
      background: transparent;
      position: fixed;
      bottom: 116px;
      right: 16px;
      z-index: 200;
    }

    .v-list-item__title:hover {
      cursor: pointer
    }

    .v-tooltip__content {
      opacity: 1 !important;
    }

    .tooltip {
      border-radius: 10px !important;
    }

    .welcome {
      border-radius: 10px;
    }

    .v-expansion-panel-header--active {
      background-color: var(--v-primary-base);
      opacity: 1;
      color: white;
    }

    .cta-button.v-btn {
      border-radius: 10px;
      /*height: 56px !important;*/
      /*padding-left: 50px !important;*/
      /*padding-right: 50px !important;*/
      text-transform: none !important;
    }

</style>
<style>
  .v-application .v-expansion-panel-header--active .v-icon, .v-application .v-expansion-panel-header--active .primary--text {
    color: white !important;
  }
</style>
